import { type Actor, type BoardState } from '@shared/board'
import { foldDiff, lineDiff } from '@shared/textdiff'
import { type RevertRecord, type TurnChangeSet } from '@shared/trust'
import { type Rect, union } from '@shared/whiteboard/geometry'
import { clsx } from 'clsx'
import {
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  RotateCcw,
  SquareDashed,
  TriangleAlert,
  Undo2,
} from 'lucide-react'
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { type OverlayMark } from '@/board/context'
import { useBoardCtx } from '@/board/context'
import { useI18n } from '@/i18n/i18n'
import { type MessageKey } from '@/i18n/messages'
import { useActorName } from './actorName'
import { type ChangeRow, changeRows, rowIds } from './changeRows'
import { itemLabel } from './describe'
import { MentionChip } from './SayText'

// The change view (DESIGN.md 信任層): what one AI turn changed on the board,
// marked on the canvas, each thing with its before/after, a replay that steps
// through them in order, and revert — of the turn or of one thing — as a new
// operation by the person. Things changed afterwards are left alone, and say so.

const REPLAY_MS = 1800

const STATUS: Record<ChangeRow['status'], MessageKey> = {
  added: 'change.added',
  changed: 'change.changed',
  removed: 'change.removed',
}

/** Where a row's thing is now, or was (removed): the rect to frame. */
function rowRect(r: ChangeRow, state: BoardState): Rect | null {
  if (r.isEdge) {
    const ends = rowIds(r)
      .map((id) => state.items.get(id))
      .filter((i) => !!i)
    return union(ends)
  }
  if (r.status === 'removed') return r.rect
  const live = state.items.get(r.id)
  return live ? { x: live.x, y: live.y, w: live.w, h: live.h } : r.rect
}

function useNames() {
  const name = useActorName()
  const { t } = useI18n()
  return (who: Actor[]) => who.map(name).join(t('act.sep'))
}

function TextDiff({ from, to }: { from: string; to: string }) {
  const { t } = useI18n()
  const lines = foldDiff(lineDiff(from, to))
  return (
    <pre className="mt-1.5 max-h-64 overflow-auto rounded-md border border-line bg-surface-2 py-1.5 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap [overflow-wrap:anywhere]">
      {lines.map((l, i) =>
        l.folded ? (
          <div key={i} className="px-2.5 text-muted italic">
            {t('change.folded', { n: l.folded })}
          </div>
        ) : (
          <div
            key={i}
            className={clsx(
              'px-2.5',
              l.op === 'add' && 'bg-(--added)/10 text-ink',
              l.op === 'del' && 'bg-danger/8 text-ink-2 line-through decoration-danger/40',
              l.op === 'same' && 'text-muted',
            )}
          >
            <span className="mr-2 inline-block w-2 select-none">
              {l.op === 'add' ? '+' : l.op === 'del' ? '−' : ' '}
            </span>
            {l.text || ' '}
          </div>
        ),
      )}
    </pre>
  )
}

function Details({ r }: { r: ChangeRow }) {
  const { t } = useI18n()
  const { client } = useBoardCtx()
  const parts: string[] = []
  for (const d of r.details) {
    if (d.kind === 'moved') parts.push(t('change.moved'))
    else if (d.kind === 'resized') parts.push(t('change.resized'))
    else if (d.kind === 'regrouped') {
      const g = (id: string | null) => {
        const it = id ? client.view.items.get(id) : null
        return it ? itemLabel(it, client.doc) : null
      }
      const to = g(d.to)
      parts.push(
        to
          ? t('change.intoGroup', { name: to })
          : d.from
            ? t('change.outOfGroup')
            : t('change.regrouped'),
      )
    } else if (d.kind === 'renamed') parts.push(t('change.renamed', { from: d.from, to: d.to }))
    else if (d.kind === 'relabelled')
      parts.push(t('change.relabelled', { from: d.from || '—', to: d.to || '—' }))
    else parts.push(t('change.other'))
  }
  if (r.text && r.status === 'changed') parts.push(t('change.text'))
  if (!parts.length) return null
  return <div className="text-[12px] text-muted">{parts.join(' · ')}</div>
}

function RowLabel({ r }: { r: ChangeRow }) {
  const { t } = useI18n()
  const { client } = useBoardCtx()
  const snap = useSyncExternalStore(client.subscribe, client.getSnapshot)
  if (r.isEdge) {
    const e = r.edge
    const end = (id: string) => {
      const it = snap.state.items.get(id)
      return it ? itemLabel(it, client.doc) || '…' : '…'
    }
    if (e && r.status !== 'removed' && snap.state.edges.has(r.id))
      return (
        <MentionChip
          mention={{ kind: 'edge', id: r.id }}
          fallback={`${end(e.from)} → ${end(e.to)}`}
        />
      )
    return (
      <span className="text-[12.5px] text-ink-2 line-through decoration-muted/60">
        {e ? `${end(e.from)} → ${end(e.to)}` : t('change.anEdge')}
      </span>
    )
  }
  const live = snap.state.items.get(r.id)
  const label =
    (live && itemLabel(live, client.doc)) || (r.item && itemLabel(r.item, client.doc)) || ''
  if (live) return <MentionChip mention={{ kind: 'item', id: r.id }} fallback={label || '…'} />
  return (
    <span className="text-[12.5px] text-ink-2 line-through decoration-muted/60">
      {label || t('change.anItem')}
    </span>
  )
}

function Row({
  r,
  index,
  current,
  reverted,
  kept,
  canRevert,
  onFocus,
  onRevert,
}: {
  r: ChangeRow
  index: number
  current: boolean
  reverted: boolean
  /** A message the turn put on the board: a turn revert keeps it. */
  kept: boolean
  canRevert: boolean
  onFocus: () => void
  onRevert: (() => void) | null
}) {
  const { t } = useI18n()
  const names = useNames()
  const [showText, setShowText] = useState(false)
  const text = r.text
  const changedAgain = text && text.current !== null && text.current !== text.after
  return (
    <li
      data-change-row={r.key}
      aria-current={current ? 'step' : undefined}
      className={clsx(
        'group/row rounded-md border px-3 py-2.5 transition-colors',
        current ? 'border-line-strong bg-surface-2' : 'border-transparent hover:bg-surface-2/60',
      )}
    >
      <div className="flex items-start gap-2">
        <span className="mt-0.5 w-4 flex-none text-right font-mono text-[11px] text-muted">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <span
              className={clsx(
                'rounded-sm px-1 py-px text-[11px] font-medium',
                r.status === 'added' && 'bg-(--added)/12 text-(--added)',
                r.status === 'changed' && 'bg-(--changed)/12 text-(--changed)',
                r.status === 'removed' && 'bg-danger/10 text-danger',
              )}
            >
              {t(STATUS[r.status])}
            </span>
            <RowLabel r={r} />
            <button
              type="button"
              onClick={onFocus}
              aria-label={t('change.show')}
              title={t('change.show')}
              className="rounded p-0.5 text-muted opacity-0 group-hover/row:opacity-100 hover:text-ink focus-visible:opacity-100"
            >
              <SquareDashed size={13} />
            </button>
          </div>
          {kept && !reverted && <div className="text-[12px] text-muted">{t('change.kept')}</div>}
          {r.intents.length > 0 && (
            <div className="text-[12.5px] text-ink-2">{r.intents.join(t('change.intentSep'))}</div>
          )}
          <Details r={r} />
          {text && r.status !== 'removed' && (
            <div>
              <button
                type="button"
                aria-expanded={showText}
                onClick={() => {
                  setShowText((v) => !v)
                }}
                className="inline-flex items-center gap-1 text-[12px] text-muted hover:text-ink"
              >
                <ChevronRight
                  size={12}
                  className={clsx('transition-transform', showText && 'rotate-90')}
                />
                {r.status === 'added' ? t('change.showText') : t('change.showDiff')}
              </button>
              {showText && (
                <>
                  <TextDiff from={text.before} to={text.after} />
                  {changedAgain && (
                    <>
                      <div className="mt-2 text-[12px] text-muted">{t('change.sinceThen')}</div>
                      <TextDiff from={text.after} to={text.current ?? ''} />
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {r.laterBy.length > 0 && !reverted && (
            <div className="flex items-start gap-1.5 text-[12px] text-(--changed)">
              <TriangleAlert size={12} className="mt-0.5 flex-none" />
              {t('change.laterBy', { who: names(r.laterBy) })}
            </div>
          )}
        </div>
        {reverted ? (
          <span className="flex-none text-[11.5px] text-muted">{t('change.reverted')}</span>
        ) : (
          onRevert && (
            <button
              type="button"
              disabled={!canRevert}
              onClick={onRevert}
              className="flex-none rounded-md px-1.5 py-0.5 text-[12px] text-muted opacity-0 group-hover/row:opacity-100 hover:bg-surface hover:text-ink focus-visible:opacity-100 disabled:cursor-default disabled:opacity-0"
            >
              {kept ? t('change.removeMessage') : t('change.revertItem')}
            </button>
          )
        )}
      </div>
    </li>
  )
}

/** A revert that happened on this turn, with its outcome and an undo. */
export function RevertNote({ r }: { r: RevertRecord }) {
  const { t } = useI18n()
  const { sessions } = useBoardCtx()
  const names = useNames()
  const [open, setOpen] = useState(false)
  const n = r.applied.filter(
    (ref) => !ref.startsWith('text:') || !r.applied.includes(`item:${ref.slice(5)}`),
  ).length
  return (
    <div className="text-[12px] text-muted" data-revert={r.id}>
      <div className="flex items-start gap-1.5 leading-5">
        <RotateCcw size={12} className="mt-1 flex-none" />
        <span className="min-w-0">
          <span className={clsx(r.undoneBy && 'line-through decoration-muted/60')}>
            {r.itemId
              ? t('revert.item', { who: names([r.by]) })
              : t('revert.turn', { who: names([r.by]), n })}
            {(r.kept ?? []).length > 0 && t('revert.kept', { n: (r.kept ?? []).length })}
            {r.skipped.length > 0 && (
              <button
                type="button"
                aria-expanded={open}
                onClick={() => {
                  setOpen((v) => !v)
                }}
                className="underline decoration-dotted underline-offset-2 hover:text-ink"
              >
                {t('revert.skipped', { n: r.skipped.length })}
              </button>
            )}
          </span>
          {r.undoneBy ? (
            <span className="ml-1.5">{t('revert.undone')}</span>
          ) : (
            <button
              type="button"
              onClick={() => {
                sessions.unrevert(r.id)
              }}
              className="ml-1 inline-flex items-center gap-1 rounded px-1 align-baseline hover:bg-surface-2 hover:text-ink"
            >
              <Undo2 size={12} className="self-center" />
              {t('revert.undo')}
            </button>
          )}
        </span>
      </div>
      {open && (
        <ul className="mt-1 ml-1.5 space-y-0.5 border-l border-line pl-3">
          {r.skipped.map((s) => (
            <li key={s.ref}>
              <SkippedLabel ref_={s.ref} />
              {s.reason === 'changed_later'
                ? t('revert.reasonLater', { who: names(s.by) })
                : s.reason === 'unsupported'
                  ? t('revert.reasonUnsupported')
                  : t('revert.reasonConflict')}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SkippedLabel({ ref_ }: { ref_: string }) {
  const { client } = useBoardCtx()
  const [kind, id = ''] = ref_.split(/:(.*)/s)
  const it = kind === 'edge' ? null : client.view.items.get(id)
  if (kind === 'edge') return <MentionChip mention={{ kind: 'edge', id }} fallback="…" />
  return (
    <MentionChip mention={{ kind: 'item', id }} fallback={it ? itemLabel(it, client.doc) : '…'} />
  )
}

export function ChangeView({
  sessionId,
  turnId,
  focusRefs,
}: {
  sessionId: string
  turnId: string
  /** Opened from an embed: these refs, and what happened to them since. */
  focusRefs: string[]
}) {
  const { t } = useI18n()
  const { sessions, client, overlay, canvas } = useBoardCtx()
  const names = useNames()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const board = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const set: TurnChangeSet | undefined = snap.changes[turnId]
  const reverts = useMemo(
    () => (snap.reverts[sessionId] ?? []).filter((r) => r.turnId === turnId),
    [snap.reverts, sessionId, turnId],
  )
  const running = !!snap.sessions.find((s) => s.id === sessionId)?.running
  const rows = useMemo(() => (set ? changeRows(set) : []), [set])
  // What a standing revert already put back: no marks, no "changed afterwards"
  // (that change was the revert).
  const revertedRefs = useMemo(
    () => new Set(reverts.filter((r) => !r.undoneBy).flatMap((r) => r.applied)),
    [reverts],
  )
  const isReverted = useCallback(
    (r: ChangeRow) => r.refs.every((ref) => revertedRefs.has(ref)),
    [revertedRefs],
  )
  const [step, setStep] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const refsKey = focusRefs.join(',')

  // Ask for the change set, and again only when something real changes: board
  // ops, card text, the conversation, or its reverts (who changed what
  // afterwards decides what a revert would skip).
  const revertCount = (snap.reverts[sessionId] ?? []).length
  const entryCount = snap.transcripts[sessionId]?.entries.length ?? 0
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const ask = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        sessions.loadChanges(sessionId, turnId, refsKey ? refsKey.split(',') : [])
      }, 500)
    }
    sessions.loadChanges(sessionId, turnId, refsKey ? refsKey.split(',') : [])
    let state = client.getSnapshot().state
    const off = client.subscribe(() => {
      const next = client.getSnapshot().state
      if (next === state) return
      state = next
      ask()
    })
    client.doc.on('update', ask)
    return () => {
      clearTimeout(timer)
      off()
      client.doc.off('update', ask)
    }
  }, [sessions, client, sessionId, turnId, refsKey, revertCount, entryCount])

  // Marks on the canvas: the step being replayed stands out.
  useEffect(() => {
    const marks: OverlayMark[] = []
    rows.forEach((r, i) => {
      if (r.isEdge || isReverted(r)) return
      const rect = rowRect(r, board.state)
      if (!rect) return
      const it = board.state.items.get(r.id) ?? r.item
      marks.push({
        id: r.key,
        status: r.status,
        rect,
        label: it ? itemLabel(it, client.doc) : '',
        current: step === i,
      })
    })
    overlay.set(marks)
  }, [rows, board.state, step, overlay, client, isReverted])
  useEffect(
    () => () => {
      overlay.set([])
    },
    [overlay],
  )

  const root = useRef<HTMLDivElement>(null)
  const frame = (rect: Rect | null, ids: string[]) => {
    if (!rect) return
    // In canvas-main the panel lies over the board: frame beside it.
    const avoid = root.current?.closest('[role="dialog"]')?.getBoundingClientRect()
    void canvas.reveal().then((api) => {
      api.fitRect(rect, avoid ? { avoid } : undefined)
      if (ids.length) api.flash(ids)
    })
  }
  // On opening, bring the whole change into view.
  const framed = useRef(false)
  useEffect(() => {
    if (framed.current || !rows.length) return
    framed.current = true
    const focus = refsKey
      ? rows.filter((r) => r.refs.some((ref) => refsKey.split(',').includes(ref)))
      : rows
    const rects = (focus.length ? focus : rows)
      .map((r) => rowRect(r, client.view))
      .filter((x): x is Rect => !!x)
    frame(union(rects), [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  const goTo = (i: number) => {
    const r = rows[i]
    if (!r) return
    setStep(i)
    frame(rowRect(r, client.view), r.status === 'removed' ? [] : rowIds(r))
    document
      .querySelector(`[data-change-row="${CSS.escape(r.key)}"]`)
      ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
  // Playing stops by itself on the last step.
  const atEnd = step !== null && step >= rows.length - 1
  const isPlaying = playing && !atEnd
  useEffect(() => {
    if (!isPlaying || step === null) return
    const timer = setTimeout(() => {
      goTo(step + 1)
    }, REPLAY_MS)
    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, step])

  /** A message the turn put on the board: a record of what was said, kept by a turn revert. */
  const isKept = (r: ChangeRow) => r.status === 'added' && r.item?.kind === 'message'
  const kept = rows.filter((r) => isKept(r) && !isReverted(r))
  const open = rows.filter((r) => !isReverted(r) && !isKept(r))
  /** Something of this row is still as the turn left it, so a revert can put it back. */
  const canPutBack = (r: ChangeRow) =>
    r.refs.some((ref) => !set?.changes.find((c) => c.ref === ref)?.laterBy.length)
  const revertible = open.filter(canPutBack)
  /** Rows with a part someone changed afterwards: that part stays as it is. */
  const touchedLater = open.filter((r) => r.laterBy.length > 0).length

  if (!set) return <p className="px-4 py-4 text-[13px] text-muted">{t('change.loading')}</p>
  if (set.unavailable)
    return (
      <div ref={root} className="px-4 py-4 text-[13px] leading-relaxed text-muted">
        {t('change.unavailable')}
      </div>
    )

  return (
    <div ref={root} className="flex min-h-0 flex-1 flex-col">
      <div className="flex-none space-y-2 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <p className="text-[13px] text-ink-2">
            {rows.length ? t('change.summary', { n: rows.length }) : t('change.none')}
          </p>
          <span className="flex-1" />
          {rows.length > 0 &&
            (step === null ? (
              <button
                type="button"
                onClick={() => {
                  goTo(0)
                  setPlaying(true)
                }}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line px-2 text-[12px] text-ink-2 hover:border-line-strong hover:text-ink"
              >
                <Play size={12} />
                {t('change.replay')}
              </button>
            ) : (
              <div
                className="flex items-center gap-1 text-[12px] text-ink-2"
                role="group"
                aria-label={t('change.replay')}
              >
                <button
                  type="button"
                  aria-label={t('change.prev')}
                  disabled={step === 0}
                  onClick={() => {
                    setPlaying(false)
                    goTo(step - 1)
                  }}
                  className="grid size-7 place-items-center rounded-md hover:bg-surface-2 disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                </button>
                <span className="min-w-14 text-center tabular-nums">
                  {t('change.step', { i: step + 1, n: rows.length })}
                </span>
                <button
                  type="button"
                  aria-label={t('change.next')}
                  disabled={step >= rows.length - 1}
                  onClick={() => {
                    setPlaying(false)
                    goTo(step + 1)
                  }}
                  className="grid size-7 place-items-center rounded-md hover:bg-surface-2 disabled:opacity-40"
                >
                  <ChevronRight size={14} />
                </button>
                <button
                  type="button"
                  aria-label={isPlaying ? t('change.pause') : t('change.play')}
                  onClick={() => {
                    if (!isPlaying && atEnd) goTo(0)
                    setPlaying(!isPlaying)
                  }}
                  className="grid size-7 place-items-center rounded-md hover:bg-surface-2"
                >
                  {isPlaying ? <Pause size={13} /> : <Play size={13} />}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPlaying(false)
                    setStep(null)
                  }}
                  className="rounded-md px-1.5 py-0.5 hover:bg-surface-2"
                >
                  {t('change.stopReplay')}
                </button>
              </div>
            ))}
        </div>
        {rows.length > 0 &&
          (running ? (
            <p className="text-[12px] text-muted">{t('change.running')}</p>
          ) : confirming ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md bg-surface-2 px-2.5 py-2 text-[12.5px]">
              <span className="text-ink-2">
                {t('change.revertConfirm', { n: revertible.length })}
                {touchedLater > 0 && t('change.revertConfirmSkip', { n: touchedLater })}
                {kept.length > 0 && t('change.revertConfirmKept', { n: kept.length })}
              </span>
              <span className="flex-1" />
              <button
                type="button"
                onClick={() => {
                  setConfirming(false)
                }}
                className="rounded-md px-2 py-0.5 text-ink-2 hover:bg-surface"
              >
                {t('change.cancel')}
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => {
                  sessions.revert(sessionId, turnId)
                  setConfirming(false)
                }}
                className="rounded-md bg-ink px-2.5 py-0.5 font-medium text-bg hover:opacity-90"
              >
                {t('change.revertDo')}
              </button>
            </div>
          ) : (
            open.length > 0 && (
              <button
                type="button"
                disabled={!revertible.length}
                onClick={() => {
                  setConfirming(true)
                }}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-line px-2 text-[12px] text-ink-2 hover:border-line-strong hover:text-ink disabled:opacity-50"
              >
                <RotateCcw size={12} />
                {t('change.revertTurn')}
              </button>
            )
          ))}
        {reverts.map((r) => (
          <RevertNote key={r.id} r={r} />
        ))}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {set.later.length > 0 && (
          <div className="mx-2 mb-2 rounded-md border border-dashed border-line-strong px-3 py-2 text-[12px] text-muted">
            <div className="mb-1 font-medium text-ink-2">{t('change.since')}</div>
            {set.later.map((l) => (
              <div key={l.ref}>
                <SkippedLabel ref_={l.ref} />
                {t('change.sinceBy', { who: names(l.by) })}
              </div>
            ))}
            <div className="mt-1">{t('change.sinceNoOld')}</div>
          </div>
        )}
        <ol className="space-y-0.5">
          {rows.map((r, i) => (
            <Fragment key={r.key}>
              <Row
                r={r}
                index={i}
                current={step === i}
                reverted={isReverted(r)}
                kept={isKept(r)}
                canRevert={!running && canPutBack(r)}
                onFocus={() => {
                  goTo(i)
                }}
                onRevert={
                  r.isEdge
                    ? null
                    : () => {
                        sessions.revert(sessionId, turnId, r.id)
                      }
                }
              />
            </Fragment>
          ))}
        </ol>
      </div>
    </div>
  )
}
